import datapoints from './datapoints';
import { getStore as getListViewStore } from 'owa-mail-list-store';
import { getRowKeysFromRowIds, getRowIdsFromRowKeys, TableView } from 'owa-mail-list-store';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import mailStore from 'owa-mail-store/lib/store/Store';
import { InstrumentationContext, selectMailStoreItemById } from 'owa-mail-store';
import flagItemService from 'owa-mail-store/lib/services/flagItemService';
import * as undoActions from 'owa-mail-undo';
import FlagType from 'owa-service/lib/contract/FlagType';
import { ActionSource, createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import logFlagInstrumentationContexts from './helpers/logFlagInstrumentationContexts';
import getTableConversationRelation from 'owa-mail-list-store/lib/utils/getTableConversationRelation';
import FlagStatus from 'owa-service/lib/contract/FlagStatus';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import { mutator, action } from 'satcheljs';
import { getAllTableViewsContainingItemsForFolder } from 'owa-mail-list-actions';
import announceFlaggedStatus from './helpers/announceFlaggedStatus';

const SetItemsFlagStateActionName = 'setItemsFlagState';

export let flagItemsStoreUpdate = action(
    'FLAG_ITEMS_STORE_UPDATE',
    (itemIds: string[], flagTypeToSet: FlagType, parentFolderId: string) => {
        return {
            itemIds,
            flagTypeToSet,
            parentFolderId,
        };
    }
);

export let flagItemStoreUpdateMutator = mutator(flagItemsStoreUpdate, actionMessage => {
    const itemIds: string[] = actionMessage.itemIds;

    // Update flag status on items
    itemIds.forEach(itemId => {
        const item = mailStore.items.get(itemId);
        if (item) {
            item.Flag = { FlagStatus: actionMessage.flagTypeToSet.FlagStatus };
        }
    });

    // Need to check all tables for the given folder
    const tableViews = getAllTableViewsContainingItemsForFolder(
        actionMessage.parentFolderId,
        true /* shouldIncludeSearchTable */
    );

    for (const tableView of tableViews) {
        // We only care about updating roll up status in conversation table
        // Item level updates are handled separately in mail store mutator
        if (tableView.tableQuery.listViewType == ReactListViewType.Conversation) {
            for (const itemId of actionMessage.itemIds) {
                rollupFlagStatusChangeOnConversation(
                    itemId,
                    tableView,
                    actionMessage.flagTypeToSet.FlagStatus
                );
            }
        }
    }
});

function rollupFlagStatusChangeOnConversation(
    itemId: string,
    tableView: TableView,
    flagStatusToSet: FlagStatus
) {
    const item = selectMailStoreItemById(itemId);
    const rowKeys = getRowKeysFromRowIds([item.ConversationId.Id], tableView);
    for (const rowKey of rowKeys) {
        const tableConversationRelation = getTableConversationRelation(rowKey, tableView.id);

        // Roll up the update to the conversation. Some notes here:
        // 1. If the conversation was not flagged and a local item in it was flagged,
        // we know the conversation should be flagged.
        // 2. If the conversation was flagged and a local item in it was unflagged,
        // we cannot know for certain that the conversation doesn't have any more local
        // flagged items. However, this is the most common case, so we'll optimistically clear the
        // flag status on the conversation. If we were wrong, notifications will correct us
        tableConversationRelation.flagStatus = flagStatusToSet;
    }
}

const setItemsFlagStateInternal = createLegacyActionWithDatapoint(
    SetItemsFlagStateActionName,
    datapoints.setItemsFlagState
)(function setItemsFlagStateInternal(
    itemIds: string[],
    instrumentationContexts: InstrumentationContext[],
    flagTypeToApply: FlagType,
    tableViewId: string,
    actionSource: ActionSource
) {
    const tableView = getListViewStore().tableViews.get(tableViewId);
    undoActions.clearLastUndoableAction();
    logFlagInstrumentationContexts(flagTypeToApply, instrumentationContexts);

    // Dispatch action to update the store state
    flagItemsStoreUpdate(itemIds, flagTypeToApply, tableView.tableQuery.folderId);

    const requestOptions = getMailboxInfo(tableView).requestOptions;
    return flagItemService(itemIds, flagTypeToApply, requestOptions);
});

/**
 * Set the specified flag state on a collection of items in listview table
 * @param itemRowKeys - the item row keys
 * @param tableViewId - the table view the conversations are in
 * @param flagType - the specified flag type to set
 */
export function setItemsFlagStateFromTable(
    itemRowKeys: string[],
    instrumentationContexts: InstrumentationContext[],
    tableViewId: string,
    flagTypeToApply: FlagType,
    actionSource: ActionSource,
    getFilteredRowKeysToPerformFlagAction: (
        rowKeys: string[],
        tableViewId: string,
        flagTypeToApply: FlagType
    ) => string[],
    override?: boolean
) {
    let rowKeysToPerformActionUpon = [];
    if (override) {
        rowKeysToPerformActionUpon = itemRowKeys;
    } else {
        const filteredRowKeys = getFilteredRowKeysToPerformFlagAction(
            itemRowKeys,
            tableViewId,
            flagTypeToApply
        );
        rowKeysToPerformActionUpon = filteredRowKeys;
    }

    if (rowKeysToPerformActionUpon.length > 0) {
        const rowIdStrings = getRowIdsFromRowKeys(rowKeysToPerformActionUpon, tableViewId);
        setItemsFlagStateInternal(
            rowIdStrings,
            instrumentationContexts,
            flagTypeToApply,
            tableViewId,
            actionSource
        );

        announceFlaggedStatus(flagTypeToApply.FlagStatus, false /* isConversation */);
    }
}

export function setItemsFlagStateFromItemIds(
    itemIds: string[],
    instrumentationContexts: InstrumentationContext[],
    flagTypeToApply: FlagType,
    tableViewId: string,
    actionSource: ActionSource
) {
    // Filter by existence in the item cache
    const filteredItemIds = itemIds.filter(itemId => !!selectMailStoreItemById(itemId));
    if (filteredItemIds.length > 0) {
        setItemsFlagStateInternal(
            filteredItemIds,
            instrumentationContexts,
            flagTypeToApply,
            tableViewId,
            actionSource
        );

        announceFlaggedStatus(flagTypeToApply.FlagStatus, false /* isConversation */);
    }
}
